import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PageHeader from "@/components/feature/PageHeader";
import SegmentedTabs from "@/components/feature/SegmentedTabs";
import EpisodeCard from "@/components/feature/EpisodeCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { episodes } from "@/mocks/episodes";
import { seasonFilters } from "@/mocks/site";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 8;

export default function Episodes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const seasonParam = Number(searchParams.get("season") || 0);
  const [page, setPage] = useState(1);

  const season = seasonFilters.some((s) => s.value === seasonParam) ? seasonParam : 0;

  useDocumentMeta({
    title: season
      ? `美国禁忌4剧集目录 - 第 ${season} 季章节列表`
      : "美国禁忌4剧集目录 - 全四季章节列表",
    description: season
      ? `美国禁忌4第 ${season} 季剧集章节列表，包含每集剧情简介、播出日期与评分，可点击进入单集详情查看剧情解析。`
      : "美国禁忌4全四季剧集章节目录，按季节筛选查看每集剧情简介、播出时间与评分，快速定位你想了解的关键章节。",
  });

  const filtered = useMemo(
    () => (season ? episodes.filter((e) => e.season === season) : episodes),
    [season]
  );

  useEffect(() => {
    setPage(1);
  }, [season]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const changeSeason = (value: string | number) => {
    const next = Number(value);
    if (next) {
      setSearchParams({ season: String(next) });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="px-4 md:px-10 py-6 md:py-10">
      <PageHeader
        crumbs={[{ label: "首页", to: "/" }, { label: "剧集章节" }]}
        eyebrow="Episodes"
        title={season ? `第 ${season} 季 · 剧集章节` : "剧集章节目录"}
        description={
          season
            ? `《美国禁忌4》第 ${season} 季共 ${filtered.length} 集，以下为按播出顺序排列的章节列表，点击任意章节可查看剧情概述、关键解析与出演阵容。`
            : "《美国禁忌4》全四季共 20 集。剧集按季播出，建议依照播出顺序观看；你也可以使用下方筛选快速定位某一季的关键章节。"
        }
        meta={`当前展示 ${filtered.length} 集 · 共 ${totalPages} 页`}
      />

      <div className="mb-6 md:mb-8">
        <SegmentedTabs
          ariaLabel="按季节筛选"
          options={seasonFilters}
          value={season}
          onChange={changeSeason}
        />
      </div>

      {paged.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 md:gap-5">
            {paged.map((ep) => (
              <EpisodeCard key={ep.id} episode={ep} />
            ))}
          </div>
          <Pagination current={currentPage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <EmptyState
          title="没有找到匹配的章节"
          description="当前筛选条件下暂无剧集章节，试试切换其他季节或重置筛选条件。"
          onReset={() => changeSeason(0)}
        />
      )}
    </div>
  );
}